<template>
  <div class="privacy container">
    <h1>Privacy Policy</h1>
    <p class="meta">
      Effective since: November 8, 2025 · Last updated: December 17, 2025
    </p>

    <hr />

    <section>
      <p>
        GAB16 (“we”, “our”, or “us”) operates the website <strong>gab16.com</strong> and various
        applications and games, including <strong>Septica Online</strong> (collectively, the
        “Service”). This Privacy Policy explains how we collect, use, and protect personal data
        when you use the Service.
      </p>
    </section>

    <nav class="toc" aria-label="Table of contents">
      <h2>What’s in this policy</h2>
      <ul>
        <li><a href="#information-we-collect">1. Information We Collect</a></li>
        <li><a href="#how-we-use">2. How We Use Your Information</a></li>
        <li><a href="#legal-bases">3. Legal Bases (EU/EEA)</a></li>
        <li><a href="#retention">4. Data Retention</a></li>
        <li><a href="#security">5. Data Security</a></li>
        <li><a href="#your-rights">6. Your Rights</a></li>
        <li><a href="#children">7. Children’s Privacy</a></li>
        <li><a href="#transfers">8. International Data Transfers</a></li>
        <li><a href="#third-parties">9. Third-Party Services</a></li>
        <li><a href="#contact">10. Contact Us</a></li>
        <li><a href="#changes">11. Changes to This Policy</a></li>
      </ul>
    </nav>

    <section id="information-we-collect">
      <h2>1. Information We Collect</h2>

      <p>
        The types of data we collect depend on the specific application and features you use.
        Some features can be used without creating an account.
      </p>

      <h3>Guest usage (no account)</h3>
      <p>
        When you use the Service without an account, we do not collect personal identifiers such
        as your name, email address, or any user provided images. We may process limited technical data
        required to operate the Service (for example, temporary session identifiers).
      </p>

      <h3>Account-based features (optional)</h3>
      <p>
        If you choose to create an account, we may collect the following information:
      </p>

      <table class="data-table" role="table">
        <thead>
          <tr>
            <th scope="col">Type</th>
            <th scope="col">Description</th>
            <th scope="col">Required</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Username</td>
            <td>Your chosen display name used in-game and on leaderboards.</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Email address</td>
            <td>Used for login, verification, account recovery, and essential service communication.</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Password</td>
            <td>Stored securely using strong one-way hashing (never in plain text).</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Avatar selection</td>
            <td>
              Players may select an avatar from a predefined set of images included in the application.
              No user-uploaded images are collected or stored.
            </td>
            <td>Optional</td>
          </tr>
        </tbody>
      </table>

      <h3>Multiplayer and technical logs</h3>
      <p>
        To operate multiplayer features, prevent abuse, and diagnose bugs or crashes, we may
        collect limited technical logs such as gameplay events, timestamps, error reports, and
        anonymized identifiers. These logs are not used for advertising and are not shared with
        third parties.
      </p>

      <p>
        We do <strong>not</strong> collect sensitive categories of personal data (such as
        government IDs, payment card details, or health information), and we do
        <strong>not</strong> sell personal data.
      </p>
    </section>

    <section id="how-we-use">
      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>Provide and operate the Service.</li>
        <li>Create and manage user accounts when applicable.</li>
        <li>Enable multiplayer gameplay, leaderboards, and in-game features.</li>
        <li>Maintain security, prevent abuse, and fix bugs.</li>
        <li>Send essential service-related communications (such as verification or password reset emails).</li>
        <li>Improve performance and reliability of the Service.</li>
      </ul>
      <p>
        We do <strong>not</strong> use personal data for targeted advertising and do
        <strong>not</strong> share personal data with advertisers.
      </p>
    </section>

    <section id="legal-bases">
      <h2>3. Legal Bases for Processing (EU/EEA)</h2>
      <ul>
        <li><strong>Contractual necessity</strong> – to provide access to the Service and its features.</li>
        <li><strong>Legitimate interests</strong> – to ensure security, stability, and bug fixing.</li>
      </ul>
    </section>

    <section id="retention">
      <h2>4. Data Retention</h2>
      <p>
        We retain personal data only as long as necessary for the purposes described above.
        Technical logs are retained for a limited period necessary for debugging and security.
        Account data is deleted upon request or account deletion, typically within
        <strong>30 days</strong>, unless a longer retention period is required by law.
      </p>
      <p>
        To exercise your rights or request account deletion, contact us at
        <a href="mailto:contact@gab16.com">contact@gab16.com</a>. We may need to verify your
        identity before fulfilling your request(The easiest way to verify this, is if you email us from the same email used when the account was created)
      </p>
    </section>

    <section id="security">
      <h2>5. Data Security</h2>
      <ul>
        <li>Passwords are protected using industry-standard one-way hashing.</li>
        <li>HTTPS is enforced for all website and API communications.</li>
        <li>Access controls and monitoring help protect against unauthorized access.</li>
      </ul>
      <p>
        While no system is perfectly secure, we take reasonable measures to protect your
        information.
      </p>
    </section>

    <section id="your-rights">
      <h2>6. Your Rights</h2>
      <p>
        <strong>EU/EEA (GDPR):</strong> You have the right to access, correct, delete, restrict,
        or object to processing of your personal data, as well as the right to data portability.
      </p>
      <p>
        <strong>Other regions:</strong> You may have similar rights under applicable local laws.
      </p>
      <p>
        To exercise your rights or request account deletion, contact us at
        <a href="mailto:contact@gab16.com">contact@gab16.com</a>. We may need to verify your
        identity before fulfilling your request(The easiest way to verify this, is if you email us from the same email used when the account was created)
      </p>
    </section>

    <section id="children">
      <h2>7. Children’s Privacy</h2>
      <p>
        Some applications, such as <strong>Septica Online</strong>, are designed to be
        family-friendly and may be used by players of all ages.
      </p>
      <p>
        Our services are not specifically directed at children, and we do not knowingly collect
        personal data from children beyond what is necessary to provide the Service.
      </p>
      <p>
        If we become aware that personal data from a child has been collected in a way that is not
        permitted by applicable law, we will take steps to delete it. Parents or guardians may
        contact us at <a href="mailto:contact@gab16.com">contact@gab16.com</a>.
      </p>
    </section>

    <section id="transfers">
      <h2>8. International Data Transfers</h2>
      <p>
        Personal data may be processed within the European Union. If data is transferred outside
        the EU, appropriate safeguards will be applied in accordance with applicable law.
      </p>
    </section>

    <section id="third-parties">
      <h2>9. Third-Party Services</h2>
      <p>
        We do not currently share personal data with third-party analytics or advertising
        providers. If this changes, we will update this policy and obtain consent where required.
      </p>
    </section>

    <section id="contact">
      <h2>10. Contact Us</h2>
      <p>
        For questions or requests regarding this Privacy Policy or your personal data:
      </p>
      <p>
        📧 <a href="mailto:contact@gab16.com">contact@gab16.com</a><br />
        🌐 <a href="https://gab16.com" target="_blank" rel="noopener">gab16.com</a>
      </p>
    </section>

    <section id="changes">
      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be communicated
        via the Service or by email. The “Last updated” date above reflects the most recent version.
      </p>
    </section>

    <div class="summary">
      <strong>In summary:</strong>
      Some features can be used without an account. When data is collected, it is limited,
      protected, never sold, and can be deleted upon request at
      <a href="mailto:contact@gab16.com">contact@gab16.com</a>.
    </div>
  </div>
</template>


<script>
export default {
  name: "PrivacyPage",
};
</script>

<style scoped>
.container {
  max-width: 860px;
  margin: 0 auto;
  padding: 24px 16px 48px;
}

h1 {
  font-size: 2rem;
  margin-bottom: 0.25rem;
}

.meta {
  color: #666;
  margin-top: 0;
}

hr {
  margin: 16px 0 24px;
}

h2 {
  font-size: 1.25rem;
  margin-top: 28px;
  margin-bottom: 12px;
}

p,
li {
  line-height: 1.6;
}

.toc {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px 16px;
  margin: 20px 0 28px;
}

.toc ul {
  margin: 0.5rem 0 0.25rem;
  padding-left: 1.25rem;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0 8px;
  font-size: 0.95rem;
}

.data-table th,
.data-table td {
  border: 1px solid #e6e6e6;
  padding: 8px 10px;
  vertical-align: top;
}

.data-table thead th {
  background: #f7f7f7;
  text-align: left;
}

.summary {
  margin-top: 28px;
  padding: 12px 14px;
  background: #f8fff4;
  border: 1px solid #d8f5c8;
  border-radius: 8px;
}
</style>
